// ClientsProjectsView.js - Enhanced with Project Health, Priority, and Advanced Features
import React, { useState, useCallback, useMemo } from 'react';
import { 
  Building2, Plus, FolderOpen, Edit2, Trash2, ChevronRight, ChevronDown, 
  Mail, Phone, Clock, MapPin, X, AlertCircle, Loader2, DollarSign,
  CheckCircle, AlertTriangle, TrendingDown, Target, Calendar, User,
  Flag, FileText, Archive, Eye, ChevronUp, Search, Filter
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import ClientDetailView from './ClientDetailView';

const ClientsProjectsView = ({ 
  clients, 
  projects, 
  timeEntries, 
  currentTenant, 
  user, 
  setClients, 
  setProjects,
  formatTime,
  teamMembers = [] // Add team members prop
}) => {
  // UI State
  const [showAddClient, setShowAddClient] = useState(false);
  const [showAddProject, setShowAddProject] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [expandedClients, setExpandedClients] = useState(new Set());
  const [selectedClientForProject, setSelectedClientForProject] = useState(null);
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'table'
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterHealth, setFilterHealth] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedClientDetail, setSelectedClientDetail] = useState(null);
  
  // Loading and error states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  
  // Client form state
  const [clientForm, setClientForm] = useState({
    name: '',
    contactEmail: '',
    contactPhone: '',
    address: '',
    notes: '',
    color: '#3B82F6'
  });
  
  // Project form state with new fields
  const [projectForm, setProjectForm] = useState({
    name: '',
    clientId: '',
    description: '',
    color: '#3B82F6',
    hourlyRate: '',
    budgetHours: '',
    budgetAmount: '',
    startDate: '',
    endDate: '',
    projectType: 'project', // account_scale, project, workstream, internal, other
    priority: 'medium', // low, medium, high, critical
    status: 'active', // active, on_hold, completed, archived
    ownerId: '', // project owner/manager
    nextInvoiceDate: '',
    risks: '',
    projectNotes: ''
  });

  const colorPresets = [
    '#3B82F6', '#10B981', '#8B5CF6', '#F59E0B', '#EF4444',
    '#06B6D4', '#84CC16', '#EC4899', '#F97316', '#6366F1'
  ];

  const projectTypes = [
    { value: 'account_scale', label: 'Account Scale', icon: '🎯' },
    { value: 'project', label: 'Project', icon: '📁' },
    { value: 'workstream', label: 'Workstream', icon: '🔄' },
    { value: 'internal', label: 'Internal', icon: '🏢' },
    { value: 'other', label: 'Other', icon: '📋' }
  ];

  const priorities = [
    { value: 'low', label: 'Low', color: 'slate', icon: Flag },
    { value: 'medium', label: 'Medium', color: 'blue', icon: Flag },
    { value: 'high', label: 'High', color: 'orange', icon: Flag },
    { value: 'critical', label: 'Critical', color: 'red', icon: Flag }
  ];

  const projectStatuses = [
    { value: 'active', label: 'Active', color: 'green' },
    { value: 'on_hold', label: 'On Hold', color: 'yellow' },
    { value: 'completed', label: 'Completed', color: 'blue' },
    { value: 'archived', label: 'Archived', color: 'slate' }
  ];

  // Calculate project health based on budget and timeline
  const calculateProjectHealth = useCallback((project, projectHours) => {
    if (!project.budget_hours) {
      return { status: 'no_budget', label: 'No Budget', color: 'slate' };
    }

    const budgetUsedPercent = (projectHours / project.budget_hours) * 100;
    
    // Calculate time elapsed
    let timeElapsedPercent = 50; // default
    if (project.start_date && project.end_date) {
      const now = new Date();
      const start = new Date(project.start_date);
      const end = new Date(project.end_date);
      const totalDays = Math.max(1, (end - start) / (1000 * 60 * 60 * 24));
      const elapsed = Math.max(0, (now - start) / (1000 * 60 * 60 * 24));
      timeElapsedPercent = Math.min((elapsed / totalDays) * 100, 100);
    }

    const variance = budgetUsedPercent - timeElapsedPercent;

    // Determine health status
    if (budgetUsedPercent > 100 || (budgetUsedPercent > timeElapsedPercent && variance > 20)) {
      return { status: 'critical', label: 'Critical', color: 'red', icon: TrendingDown };
    } else if (budgetUsedPercent > 90 || (budgetUsedPercent > timeElapsedPercent && variance > 10)) {
      return { status: 'warning', label: 'At Risk', color: 'yellow', icon: AlertTriangle };
    } else {
      return { status: 'healthy', label: 'Healthy', color: 'green', icon: CheckCircle };
    }
  }, []);

  // Form validation
  const validateClientForm = useCallback(() => {
    const errors = {};
    
    if (!clientForm.name.trim()) {
      errors.name = 'Client name is required';
    } else if (clientForm.name.trim().length < 2) {
      errors.name = 'Client name must be at least 2 characters';
    }
    
    if (clientForm.contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clientForm.contactEmail)) {
      errors.contactEmail = 'Please enter a valid email address';
    }
    
    if (clientForm.contactPhone && !/^[\d\s\-+()]+$/.test(clientForm.contactPhone)) {
      errors.contactPhone = 'Please enter a valid phone number';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [clientForm]);

  const validateProjectForm = useCallback(() => {
    const errors = {};
    
    if (!projectForm.name.trim()) {
      errors.name = 'Project name is required';
    }
    
    if (!projectForm.clientId) {
      errors.clientId = 'Please select a client';
    }
    
    if (projectForm.hourlyRate && (isNaN(projectForm.hourlyRate) || parseFloat(projectForm.hourlyRate) < 0)) {
      errors.hourlyRate = 'Hourly rate must be a positive number';
    }
    
    if (projectForm.budgetHours && (isNaN(projectForm.budgetHours) || parseFloat(projectForm.budgetHours) <= 0)) {
      errors.budgetHours = 'Budget hours must be a positive number';
    }
    
    if (projectForm.startDate && projectForm.endDate) {
      const start = new Date(projectForm.startDate);
      const end = new Date(projectForm.endDate);
      if (end < start) {
        errors.endDate = 'End date must be after start date';
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [projectForm]);

  const resetClientForm = useCallback(() => {
    setClientForm({
      name: '',
      contactEmail: '',
      contactPhone: '',
      address: '',
      notes: '',
      color: '#3B82F6'
    });
    setFormErrors({});
    setError(null);
  }, []);

  const resetProjectForm = useCallback(() => {
    setProjectForm({
      name: '',
      clientId: '',
      description: '',
      color: '#3B82F6',
      hourlyRate: '',
      budgetHours: '',
      budgetAmount: '',
      startDate: '',
      endDate: '',
      projectType: 'project',
      priority: 'medium',
      status: 'active',
      ownerId: '',
      nextInvoiceDate: '',
      risks: '',
      projectNotes: ''
    });
    setFormErrors({});
    setError(null);
  }, []);

  const toggleClientExpansion = useCallback((clientId) => {
    setExpandedClients(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(clientId)) {
        newExpanded.delete(clientId);
      } else {
        newExpanded.add(clientId);
      }
      return newExpanded;
    });
  }, []);

  // CRUD Operations
  const handleCreateClient = async (e) => {
    e.preventDefault();
    setError(null);
    
    if (!validateClientForm()) return;
    
    setIsSubmitting(true);

    try {
      const { data, error: supabaseError } = await supabase
        .from('clients')
        .insert([{
          tenant_id: currentTenant.id,
          name: clientForm.name.trim(),
          contact_email: clientForm.contactEmail.trim() || null,
          contact_phone: clientForm.contactPhone.trim() || null,
          address: clientForm.address.trim() || null,
          notes: clientForm.notes.trim() || null,
          color: clientForm.color,
          created_by: user.id,
          status: 'active'
        }])
        .select()
        .single();

      if (supabaseError) throw supabaseError;

      setClients(prev => [...prev, data]);
      resetClientForm();
      setShowAddClient(false);
    } catch (err) {
      console.error('Error creating client:', err);
      setError(err.message || 'Failed to create client. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateClient = async (e) => {
    e.preventDefault();
    setError(null);
    
    if (!validateClientForm() || !editingClient) return;
    
    setIsSubmitting(true);

    try {
      const { data, error: supabaseError } = await supabase
        .from('clients')
        .update({
          name: clientForm.name.trim(),
          contact_email: clientForm.contactEmail.trim() || null,
          contact_phone: clientForm.contactPhone.trim() || null,
          address: clientForm.address.trim() || null,
          notes: clientForm.notes.trim() || null,
          color: clientForm.color
        })
        .eq('id', editingClient.id)
        .select()
        .single();

      if (supabaseError) throw supabaseError;

      setClients(prev => prev.map(client => 
        client.id === editingClient.id ? data : client
      ));
      resetClientForm();
      setEditingClient(null);
    } catch (err) {
      console.error('Error updating client:', err);
      setError(err.message || 'Failed to update client. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditClient = useCallback((client) => {
    setEditingClient(client);
    setClientForm({
      name: client.name,
      contactEmail: client.contact_email || '',
      contactPhone: client.contact_phone || '',
      address: client.address || '',
      notes: client.notes || '',
      color: client.color || '#3B82F6'
    });
    setFormErrors({});
    setError(null);
  }, []);

  const handleDeleteClient = async (clientId) => {
    const clientProjects = projects.filter(p => p.client_id === clientId);
    
    if (clientProjects.length > 0) {
      if (!window.confirm(`This client has ${clientProjects.length} project(s). Deleting will archive all associated projects. Continue?`)) {
        return;
      }
    } else if (!window.confirm('Are you sure you want to delete this client?')) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      if (clientProjects.length > 0) {
        const { error: projectError } = await supabase
          .from('projects')
          .update({ status: 'archived' })
          .eq('client_id', clientId);

        if (projectError) throw projectError;
      }

      const { error: clientError } = await supabase
        .from('clients')
        .update({ status: 'inactive' })
        .eq('id', clientId);

      if (clientError) throw clientError;

      setClients(prev => prev.filter(client => client.id !== clientId));
      setProjects(prev => prev.filter(project => project.client_id !== clientId));
    } catch (err) {
      console.error('Error deleting client:', err);
      setError(err.message || 'Failed to delete client. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    setError(null);
    
    if (!validateProjectForm()) return;

    setIsSubmitting(true);

    try {
      const { data, error: supabaseError } = await supabase
        .from('projects')
        .insert([{
          tenant_id: currentTenant.id,
          client_id: projectForm.clientId,
          name: projectForm.name.trim(),
          description: projectForm.description.trim() || null,
          color: projectForm.color,
          hourly_rate: projectForm.hourlyRate ? parseFloat(projectForm.hourlyRate) : 0,
          budget_hours: projectForm.budgetHours ? parseFloat(projectForm.budgetHours) : null,
          budget_amount: projectForm.budgetAmount ? parseFloat(projectForm.budgetAmount) : null,
          start_date: projectForm.startDate || null,
          end_date: projectForm.endDate || null,
          project_type: projectForm.projectType,
          priority: projectForm.priority,
          status: projectForm.status,
          owner_id: projectForm.ownerId || null,
          next_invoice_date: projectForm.nextInvoiceDate || null,
          risks: projectForm.risks.trim() || null,
          project_notes: projectForm.projectNotes.trim() || null,
          created_by: user.id
        }])
        .select(`
          *,
          clients (name),
          owner:owner_id (full_name, email)
        `)
        .single();

      if (supabaseError) throw supabaseError;

      setProjects(prev => [...prev, data]);
      resetProjectForm();
      setShowAddProject(false);
      setSelectedClientForProject(null);
      setExpandedClients(prev => new Set([...prev, projectForm.clientId]));
    } catch (err) {
      console.error('Error creating project:', err);
      setError(err.message || 'Failed to create project. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateProject = async (e) => {
    e.preventDefault();
    setError(null);
    
    if (!validateProjectForm() || !editingProject) return;

    setIsSubmitting(true);

    try {
      const { data, error: supabaseError } = await supabase
        .from('projects')
        .update({
          client_id: projectForm.clientId,
          name: projectForm.name.trim(),
          description: projectForm.description.trim() || null,
          color: projectForm.color,
          hourly_rate: projectForm.hourlyRate ? parseFloat(projectForm.hourlyRate) : 0,
          budget_hours: projectForm.budgetHours ? parseFloat(projectForm.budgetHours) : null,
          budget_amount: projectForm.budgetAmount ? parseFloat(projectForm.budgetAmount) : null,
          start_date: projectForm.startDate || null,
          end_date: projectForm.endDate || null,
          project_type: projectForm.projectType,
          priority: projectForm.priority,
          status: projectForm.status,
          owner_id: projectForm.ownerId || null,
          next_invoice_date: projectForm.nextInvoiceDate || null,
          risks: projectForm.risks.trim() || null,
          project_notes: projectForm.projectNotes.trim() || null
        })
        .eq('id', editingProject.id)
        .select(`
          *,
          clients (name),
          owner:owner_id (full_name, email)
        `)
        .single();

      if (supabaseError) throw supabaseError;

      setProjects(prev => prev.map(project => 
        project.id === editingProject.id ? data : project
      ));
      resetProjectForm();
      setEditingProject(null);
    } catch (err) {
      console.error('Error updating project:', err);
      setError(err.message || 'Failed to update project. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditProject = useCallback((project) => {
    setEditingProject(project);
    setProjectForm({
      name: project.name,
      clientId: project.client_id,
      description: project.description || '',
      color: project.color || '#3B82F6',
      hourlyRate: project.hourly_rate?.toString() || '',
      budgetHours: project.budget_hours?.toString() || '',
      budgetAmount: project.budget_amount?.toString() || '',
      startDate: project.start_date || '',
      endDate: project.end_date || '',
      projectType: project.project_type || 'fixed_price',
      priority: project.priority || 'medium',
      status: project.status || 'active',
      ownerId: project.owner_id || '',
      nextInvoiceDate: project.next_invoice_date || '',
      risks: project.risks || '',
      projectNotes: project.project_notes || ''
    });
    setFormErrors({});
    setError(null);
  }, []);

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Are you sure you want to archive this project?')) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const { error: supabaseError } = await supabase
        .from('projects')
        .update({ status: 'archived' })
        .eq('id', projectId);

      if (supabaseError) throw supabaseError;

      setProjects(prev => prev.filter(project => project.id !== projectId));
    } catch (err) {
      console.error('Error deleting project:', err);
      setError(err.message || 'Failed to archive project. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddProjectToClient = useCallback((clientId) => {
    setSelectedClientForProject(clientId);
    setProjectForm(prev => ({ ...prev, clientId }));
    setShowAddProject(true);
    setFormErrors({});
    setError(null);
  }, []);

  // Statistics calculations
  const getClientStats = useMemo(() => (clientId) => {
    const clientProjects = projects.filter(p => p.client_id === clientId);
    const clientHours = timeEntries
      .filter(entry => entry.client_id === clientId)
      .reduce((sum, entry) => sum + (entry.duration_hours || 0), 0);
    
    const activeProjects = clientProjects.filter(p => p.status === 'active').length;
    const projectsAtRisk = clientProjects.filter(p => {
      const hours = getProjectStats(p.id).hours;
      const health = calculateProjectHealth(p, hours);
      return health.status === 'warning' || health.status === 'critical';
    }).length;
    
    return {
      projectCount: clientProjects.length,
      activeProjects,
      projectsAtRisk,
      totalHours: clientHours
    };
  }, [projects, timeEntries, calculateProjectHealth]);

  const getProjectStats = useMemo(() => (projectId) => {
    const projectEntries = timeEntries.filter(entry => entry.project_id === projectId);
    const hours = projectEntries.reduce((sum, entry) => sum + (entry.duration_hours || 0), 0);
    const billableHours = projectEntries
      .filter(entry => entry.is_billable)
      .reduce((sum, entry) => sum + (entry.duration_hours || 0), 0);
    return { hours, billableHours };
  }, [timeEntries]);

  // Filtered projects
  const filteredClients = useMemo(() => {
    return clients.filter(client => {
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const matchesClient = client.name.toLowerCase().includes(search) ||
                            client.contact_email?.toLowerCase().includes(search);
        
        const clientProjects = projects.filter(p => p.client_id === client.id);
        const matchesProject = clientProjects.some(p => 
          p.name.toLowerCase().includes(search)
        );
        
        if (!matchesClient && !matchesProject) return false;
      }

      if (filterPriority !== 'all' || filterHealth !== 'all') {
        const clientProjects = projects.filter(p => p.client_id === client.id);
        
        if (clientProjects.length === 0) return false;
        
        return clientProjects.some(project => {
          const hours = getProjectStats(project.id).hours;
          const health = calculateProjectHealth(project, hours);
          
          const matchesPriority = filterPriority === 'all' || project.priority === filterPriority;
          const matchesHealth = filterHealth === 'all' || health.status === filterHealth;
          
          return matchesPriority && matchesHealth;
        });
      }

      return true;
    });
  }, [clients, projects, searchTerm, filterPriority, filterHealth, getProjectStats, calculateProjectHealth]);

  // Components
  const ErrorAlert = ({ message }) => (
    <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
      <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
      <div className="flex-1">
        <p className="text-sm text-red-800">{message}</p>
      </div>
      <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
        <X className="w-4 h-4" />
      </button>
    </div>
  );

  const InputError = ({ error }) => (
    error ? (
      <p className="mt-1 text-sm text-red-600 flex items-center space-x-1">
        <AlertCircle className="w-4 h-4" />
        <span>{error}</span>
      </p>
    ) : null
  );

  const PriorityBadge = ({ priority }) => {
    const config = priorities.find(p => p.value === priority) || priorities[1];
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded text-xs font-medium bg-${config.color}-100 text-${config.color}-700`}>
        <Icon className="w-3 h-3" />
        <span>{config.label}</span>
      </span>
    );
  };

  const HealthBadge = ({ health }) => {
    const Icon = health.icon || Target;
    return (
      <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded text-xs font-medium bg-${health.color}-100 text-${health.color}-700`}>
        <Icon className="w-3 h-3" />
        <span>{health.label}</span>
      </span>
    );
  };

  const StatusBadge = ({ status }) => {
    const config = projectStatuses.find(s => s.value === status) || projectStatuses[0];
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-${config.color}-100 text-${config.color}-700`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="space-y-6">
    {selectedClientDetail ? (
  <ClientDetailView
    client={selectedClientDetail}
    projects={projects.filter(p => p.client_id === selectedClientDetail.id)}
    timeEntries={timeEntries.filter(e => e.client_id === selectedClientDetail.id)}
	currentTenant={currentTenant}  
    user={user}                     
    teamMembers={teamMembers}
    onBack={() => setSelectedClientDetail(null)}
    onEditClient={(client) => {
      setSelectedClientDetail(null);
      handleEditClient(client);
    }}
    formatTime={formatTime}
    calculateProjectHealth={calculateProjectHealth}
  />
) : (
        <>
      {/* Header */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-slate-800 flex items-center space-x-3">
              <Building2 className="w-7 h-7 text-blue-600" />
              <span>Clients & Projects</span>
            </h2>
            <p className="text-slate-600 mt-1">Manage clients, projects, and track their health</p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => setShowAddProject(true)}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={clients.length === 0 || isSubmitting}
            >
              <FolderOpen className="w-4 h-4" />
              <span>Add Project</span>
            </button>
            <button
              onClick={() => setShowAddClient(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting}
            >
              <Plus className="w-4 h-4" />
              <span>Add Client</span>
            </button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="flex items-center space-x-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search clients or projects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-2 border rounded-lg flex items-center space-x-2 transition-colors ${
              showFilters ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-slate-300 text-slate-700 hover:bg-slate-50'
            }`}
          >
            <Filter className="w-4 h-4" />
            <span>Filters</span>
          </button>
        </div>

        {/* Filter Options */}
        {showFilters && (
          <div className="mt-4 p-4 bg-slate-50 rounded-lg grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Priority</label>
              <select
                value={filterPriority}
                onChange={(e) => setFilterPriority(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Priorities</option>
                {priorities.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Health Status</label>
              <select
                value={filterHealth}
                onChange={(e) => setFilterHealth(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Statuses</option>
                <option value="healthy">Healthy</option>
                <option value="warning">At Risk</option>
                <option value="critical">Critical</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Global Error Display */}
      {error && <ErrorAlert message={error} />}

      {/* Clients with Nested Projects */}
      {filteredClients.length > 0 ? (
        <div className="space-y-4">
          {filteredClients.map(client => {
            const stats = getClientStats(client.id);
            const clientProjects = projects.filter(p => p.client_id === client.id);
            const isExpanded = expandedClients.has(client.id);

            return (
              <div key={client.id} className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <div 
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: client.color }}
                        />
                        <h3 className="text-lg font-semibold text-slate-800">{client.name}</h3>
                      </div>
                      
                      <div className="flex flex-wrap gap-4 text-sm text-slate-600 mb-3">
                        {client.contact_email && (
                          <div className="flex items-center space-x-2">
                            <Mail className="w-4 h-4 text-slate-400" />
                            <a href={`mailto:${client.contact_email}`} className="hover:text-blue-600 transition-colors">
                              {client.contact_email}
                            </a>
                          </div>
                        )}
                        {client.contact_phone && (
                          <div className="flex items-center space-x-2">
                            <Phone className="w-4 h-4 text-slate-400" />
                            <a href={`tel:${client.contact_phone}`} className="hover:text-blue-600 transition-colors">
                              {client.contact_phone}
                            </a>
                          </div>
                        )}
                        {client.address && (
                          <div className="flex items-center space-x-2">
                            <MapPin className="w-4 h-4 text-slate-400" />
                            <span>{client.address}</span>
                          </div>
                        )}
                      </div>

                      {/* Client Stats */}
                      <div className="flex flex-wrap gap-4 text-sm">
                        <div className="flex items-center space-x-2 text-slate-600">
                          <Clock className="w-4 h-4 text-slate-400" />
                          <span>{formatTime(stats.totalHours)}</span>
                        </div>
                        <div className="flex items-center space-x-2 text-slate-600">
                          <FolderOpen className="w-4 h-4 text-slate-400" />
                          <span>{stats.activeProjects} active</span>
                        </div>
                        {stats.projectsAtRisk > 0 && (
                          <div className="flex items-center space-x-2 text-orange-600">
                            <AlertTriangle className="w-4 h-4" />
                            <span>{stats.projectsAtRisk} at risk</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setSelectedClientDetail(client)}
                        className="p-2 text-slate-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                        title="View client details"
                        disabled={isSubmitting}
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEditClient(client)}
                        className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit client"
                        disabled={isSubmitting}
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteClient(client.id)}
                        className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete client"
                        disabled={isSubmitting}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {client.notes && (
                    <div className="mb-4 p-3 bg-slate-50 rounded-lg">
                      <p className="text-sm text-slate-600">{client.notes}</p>
                    </div>
                  )}

                  {/* Projects Toggle */}
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => toggleClientExpansion(client.id)}
                      className="flex items-center space-x-2 text-slate-700 hover:text-slate-900 transition-colors"
                    >
                      <div className="flex items-center space-x-2">
                        {isExpanded ? (
                          <ChevronDown className="w-5 h-5 text-slate-500" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-slate-500" />
                        )}
                        <FolderOpen className="w-5 h-5 text-slate-500" />
                        <span className="font-medium">
                          Projects ({stats.projectCount})
                        </span>
                      </div>
                    </button>
                    
                    <button
                      onClick={() => handleAddProjectToClient(client.id)}
                      className="flex items-center space-x-2 text-green-600 hover:text-green-700 hover:bg-green-50 px-3 py-1 rounded-lg transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={isSubmitting}
                    >
                      <Plus className="w-4 h-4" />
                      <span>Add Project</span>
                    </button>
                  </div>
                </div>

                {/* Projects List (Collapsible) */}
                {isExpanded && (
                  <div className="border-t border-slate-200 bg-slate-50">
                    {clientProjects.length > 0 ? (
                      <div className="p-4 space-y-3">
                        {clientProjects.map(project => {
                          const projectStats = getProjectStats(project.id);
                          const health = calculateProjectHealth(project, projectStats.hours);
                          const budgetProgress = project.budget_hours 
                            ? (projectStats.hours / project.budget_hours) * 100 
                            : 0;

                          return (
                            <div 
                              key={project.id}
                              className="bg-white p-4 rounded-lg border border-slate-200 hover:shadow-md transition-shadow"
                            >
                              {/* Project Header */}
                              <div className="flex items-start justify-between mb-3">
                                <div className="flex-1">
                                  <div className="flex items-center space-x-2 mb-2">
                                    <div 
                                      className="w-2 h-2 rounded-full"
                                      style={{ backgroundColor: project.color }}
                                    />
                                    <h4 className="font-medium text-slate-800">{project.name}</h4>
                                    <PriorityBadge priority={project.priority || 'medium'} />
                                    {project.status && <StatusBadge status={project.status} />}
                                  </div>
                                  
                                  {project.description && (
                                    <p className="text-sm text-slate-600 mb-2">{project.description}</p>
                                  )}
                                  
                                  {/* Project Meta */}
                                  <div className="flex flex-wrap gap-3 text-xs text-slate-500">
                                    {project.project_type && (
                                      <span className="flex items-center space-x-1">
                                        <span>{projectTypes.find(t => t.value === project.project_type)?.icon}</span>
                                        <span>{projectTypes.find(t => t.value === project.project_type)?.label}</span>
                                      </span>
                                    )}
                                    {project.hourly_rate > 0 && (
                                      <span>${project.hourly_rate}/hr</span>
                                    )}
                                    {project.budget_hours && (
                                      <span>{projectStats.hours.toFixed(1)}h / {project.budget_hours}h</span>
                                    )}
                                    {project.start_date && (
                                      <span>📅 {new Date(project.start_date).toLocaleDateString()}</span>
                                    )}
                                    {project.owner && (
                                      <span className="flex items-center space-x-1">
                                        <User className="w-3 h-3" />
                                        <span>{project.owner.full_name}</span>
                                      </span>
                                    )}
                                  </div>
                                </div>

                                <div className="flex items-center space-x-2">
                                  <HealthBadge health={health} />
                                  <button
                                    onClick={() => handleEditProject(project)}
                                    className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                    title="Edit project"
                                    disabled={isSubmitting}
                                  >
                                    <Edit2 className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteProject(project.id)}
                                    className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                    title="Archive project"
                                    disabled={isSubmitting}
                                  >
                                    <Archive className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </div>

                              {/* Budget Progress Bar */}
                              {project.budget_hours && (
                                <div>
                                  <div className="flex items-center justify-between text-xs text-slate-600 mb-1">
                                    <span>Budget Progress</span>
                                    <span className="font-medium">{budgetProgress.toFixed(0)}%</span>
                                  </div>
                                  <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                                    <div 
                                      className={`h-full transition-all ${
                                        health.status === 'critical' ? 'bg-red-500' : 
                                        health.status === 'warning' ? 'bg-yellow-500' : 'bg-green-500'
                                      }`}
                                      style={{ width: `${Math.min(budgetProgress, 100)}%` }}
                                    />
                                  </div>
                                </div>
                              )}

                              {/* Risks & Notes */}
                              {(project.risks || project.next_invoice_date) && (
                                <div className="mt-3 pt-3 border-t border-slate-200 space-y-2">
                                  {project.risks && (
                                    <div className="flex items-start space-x-2 text-xs">
                                      <AlertTriangle className="w-3.5 h-3.5 text-orange-500 flex-shrink-0 mt-0.5" />
                                      <span className="text-slate-600">{project.risks}</span>
                                    </div>
                                  )}
                                  {project.next_invoice_date && (
                                    <div className="flex items-center space-x-2 text-xs text-slate-600">
                                      <DollarSign className="w-3.5 h-3.5 text-green-500" />
                                      <span>Next invoice: {new Date(project.next_invoice_date).toLocaleDateString()}</span>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="p-8 text-center">
                        <FolderOpen className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                        <p className="text-slate-500 text-sm mb-3">No projects yet for this client</p>
                        <button
                          onClick={() => handleAddProjectToClient(client.id)}
                          className="text-green-600 hover:text-green-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={isSubmitting}
                        >
                          Create first project
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white rounded-2xl p-12 shadow-sm border border-slate-200 text-center">
          <Building2 className="w-16 h-16 mx-auto mb-4 text-slate-300" />
          <h3 className="text-lg font-medium text-slate-800 mb-2">
            {searchTerm || filterPriority !== 'all' || filterHealth !== 'all' 
              ? 'No clients match your filters' 
              : 'No clients yet'}
          </h3>
          <p className="text-slate-600 mb-6">
            {searchTerm || filterPriority !== 'all' || filterHealth !== 'all'
              ? 'Try adjusting your search or filters'
              : 'Start by adding your first client to organize your projects'}
          </p>
          {!searchTerm && filterPriority === 'all' && filterHealth === 'all' && (
            <button
              onClick={() => setShowAddClient(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg flex items-center space-x-2 mx-auto transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting}
            >
              <Plus className="w-4 h-4" />
              <span>Add First Client</span>
            </button>
          )}
        </div>
      )}

      {/* Add/Edit Client Modal */}
      {(showAddClient || editingClient) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-slate-800">
                {editingClient ? 'Edit Client' : 'Add New Client'}
              </h3>
              <button
                onClick={() => {
                  setShowAddClient(false);
                  setEditingClient(null);
                  resetClientForm();
                }}
                className="text-slate-400 hover:text-slate-600"
                disabled={isSubmitting}
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {error && <ErrorAlert message={error} />}
            
            <form onSubmit={editingClient ? handleUpdateClient : handleCreateClient} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Client Name *</label>
                <input
                  type="text"
                  value={clientForm.name}
                  onChange={(e) => setClientForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Acme Corporation"
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                    formErrors.name ? 'border-red-300 focus:ring-red-500' : 'border-slate-300 focus:ring-blue-500'
                  }`}
                  disabled={isSubmitting}
                  required
                />
                <InputError error={formErrors.name} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Contact Email</label>
                  <input
                    type="email"
                    value={clientForm.contactEmail}
                    onChange={(e) => setClientForm(prev => ({ ...prev, contactEmail: e.target.value }))}
                    placeholder="contact@acme.com"
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                      formErrors.contactEmail ? 'border-red-300 focus:ring-red-500' : 'border-slate-300 focus:ring-blue-500'
                    }`}
                    disabled={isSubmitting}
                  />
                  <InputError error={formErrors.contactEmail} />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Contact Phone</label>
                  <input
                    type="tel"
                    value={clientForm.contactPhone}
                    onChange={(e) => setClientForm(prev => ({ ...prev, contactPhone: e.target.value }))}
                    placeholder="+1 (555) 123-4567"
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                      formErrors.contactPhone ? 'border-red-300 focus:ring-red-500' : 'border-slate-300 focus:ring-blue-500'
                    }`}
                    disabled={isSubmitting}
                  />
                  <InputError error={formErrors.contactPhone} />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Address</label>
                <input
                  type="text"
                  value={clientForm.address}
                  onChange={(e) => setClientForm(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="123 Main St, City, State"
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Notes</label>
                <textarea
                  value={clientForm.notes}
                  onChange={(e) => setClientForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Additional notes about this client..."
                  rows={3}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Client Color</label>
                <div className="flex items-center space-x-4">
                  <input
                    type="color"
                    value={clientForm.color}
                    onChange={(e) => setClientForm(prev => ({ ...prev, color: e.target.value }))}
                    className="w-16 h-12 border border-slate-300 rounded-lg cursor-pointer"
                    disabled={isSubmitting}
                  />
                  <div className="flex space-x-2">
                    {colorPresets.map(color => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setClientForm(prev => ({ ...prev, color }))}
                        className={`w-8 h-8 rounded-full border-2 transition-all ${
                          clientForm.color === color ? 'border-slate-800 scale-110' : 'border-slate-300'
                        }`}
                        style={{ backgroundColor: color }}
                        disabled={isSubmitting}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-4 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddClient(false);
                    setEditingClient(null);
                    resetClientForm();
                  }}
                  className="px-6 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isSubmitting}
                >
                  {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  <span>{editingClient ? 'Update Client' : 'Add Client'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add/Edit Project Modal */}
      {(showAddProject || editingProject) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-slate-800">
                {editingProject ? 'Edit Project' : 'Add New Project'}
              </h3>
              <button
                onClick={() => {
                  setShowAddProject(false);
                  setEditingProject(null);
                  setSelectedClientForProject(null);
                  resetProjectForm();
                }}
                className="text-slate-400 hover:text-slate-600"
                disabled={isSubmitting}
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {error && <ErrorAlert message={error} />}
            
            <form onSubmit={editingProject ? handleUpdateProject : handleCreateProject} className="space-y-6">
              {/* Basic Information */}
              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center space-x-2">
                  <FileText className="w-4 h-4" />
                  <span>Basic Information</span>
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Project Name *</label>
                    <input
                      type="text"
                      value={projectForm.name}
                      onChange={(e) => setProjectForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Website Redesign"
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                        formErrors.name ? 'border-red-300 focus:ring-red-500' : 'border-slate-300 focus:ring-green-500'
                      }`}
                      disabled={isSubmitting}
                      required
                    />
                    <InputError error={formErrors.name} />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Client *</label>
                    <select
                      value={projectForm.clientId}
                      onChange={(e) => setProjectForm(prev => ({ ...prev, clientId: e.target.value }))}
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 bg-white ${
                        formErrors.clientId ? 'border-red-300 focus:ring-red-500' : 'border-slate-300 focus:ring-green-500'
                      }`}
                      disabled={selectedClientForProject !== null || isSubmitting}
                      required
                    >
                      <option value="">Select a client</option>
                      {clients.map(client => (
                        <option key={client.id} value={client.id}>{client.name}</option>
                      ))}
                    </select>
                    <InputError error={formErrors.clientId} />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Description</label>
                  <textarea
                    value={projectForm.description}
                    onChange={(e) => setProjectForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Brief description of the project..."
                    rows={2}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              {/* Project Settings */}
              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center space-x-2">
                  <Target className="w-4 h-4" />
                  <span>Project Settings</span>
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Project Type</label>
                    <select
                      value={projectForm.projectType}
                      onChange={(e) => setProjectForm(prev => ({ ...prev, projectType: e.target.value }))}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
                      disabled={isSubmitting}
                    >
                      {projectTypes.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.icon} {type.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Priority</label>
                    <select
                      value={projectForm.priority}
                      onChange={(e) => setProjectForm(prev => ({ ...prev, priority: e.target.value }))}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
                      disabled={isSubmitting}
                    >
                      {priorities.map(p => (
                        <option key={p.value} value={p.value}>{p.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Status</label>
                    <select
                      value={projectForm.status}
                      onChange={(e) => setProjectForm(prev => ({ ...prev, status: e.target.value }))}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
                      disabled={isSubmitting}
                    >
                      {projectStatuses.map(s => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Project Owner</label>
                    <select
                      value={projectForm.ownerId}
                      onChange={(e) => setProjectForm(prev => ({ ...prev, ownerId: e.target.value }))}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
                      disabled={isSubmitting}
                    >
                      <option value="">No owner assigned</option>
                      {teamMembers.map(member => (
                        <option key={member.id} value={member.id}>{member.full_name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Project Color</label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={projectForm.color}
                        onChange={(e) => setProjectForm(prev => ({ ...prev, color: e.target.value }))}
                        className="w-12 h-12 border border-slate-300 rounded-lg cursor-pointer"
                        disabled={isSubmitting}
                      />
                      <div className="flex space-x-1">
                        {colorPresets.slice(0, 6).map(color => (
                          <button
                            key={color}
                            type="button"
                            onClick={() => setProjectForm(prev => ({ ...prev, color }))}
                            className={`w-6 h-6 rounded-full border transition-all ${
                              projectForm.color === color ? 'border-slate-800 scale-110' : 'border-slate-300'
                            }`}
                            style={{ backgroundColor: color }}
                            disabled={isSubmitting}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Budget & Billing */}
              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center space-x-2">
                  <DollarSign className="w-4 h-4" />
                  <span>Budget & Billing</span>
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Hourly Rate ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={projectForm.hourlyRate}
                      onChange={(e) => setProjectForm(prev => ({ ...prev, hourlyRate: e.target.value }))}
                      placeholder="150.00"
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                        formErrors.hourlyRate ? 'border-red-300 focus:ring-red-500' : 'border-slate-300 focus:ring-green-500'
                      }`}
                      disabled={isSubmitting}
                    />
                    <InputError error={formErrors.hourlyRate} />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Budget Hours</label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      value={projectForm.budgetHours}
                      onChange={(e) => setProjectForm(prev => ({ ...prev, budgetHours: e.target.value }))}
                      placeholder="100"
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                        formErrors.budgetHours ? 'border-red-300 focus:ring-red-500' : 'border-slate-300 focus:ring-green-500'
                      }`}
                      disabled={isSubmitting}
                    />
                    <InputError error={formErrors.budgetHours} />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Budget Amount ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={projectForm.budgetAmount}
                      onChange={(e) => setProjectForm(prev => ({ ...prev, budgetAmount: e.target.value }))}
                      placeholder="15000.00"
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                        formErrors.budgetAmount ? 'border-red-300 focus:ring-red-500' : 'border-slate-300 focus:ring-green-500'
                      }`}
                      disabled={isSubmitting}
                    />
                    <InputError error={formErrors.budgetAmount} />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Next Invoice</label>
                    <input
                      type="date"
                      value={projectForm.nextInvoiceDate}
                      onChange={(e) => setProjectForm(prev => ({ ...prev, nextInvoiceDate: e.target.value }))}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
              </div>

              {/* Timeline */}
              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center space-x-2">
                  <Calendar className="w-4 h-4" />
                  <span>Timeline</span>
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Start Date</label>
                    <input
                      type="date"
                      value={projectForm.startDate}
                      onChange={(e) => setProjectForm(prev => ({ ...prev, startDate: e.target.value }))}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      disabled={isSubmitting}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">End Date</label>
                    <input
                      type="date"
                      value={projectForm.endDate}
                      onChange={(e) => setProjectForm(prev => ({ ...prev, endDate: e.target.value }))}
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                        formErrors.endDate ? 'border-red-300 focus:ring-red-500' : 'border-slate-300 focus:ring-green-500'
                      }`}
                      disabled={isSubmitting}
                    />
                    <InputError error={formErrors.endDate} />
                  </div>
                </div>
              </div>

              {/* Additional Information */}
              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center space-x-2">
                  <FileText className="w-4 h-4" />
                  <span>Additional Information</span>
                </h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Risks & Concerns
                    </label>
                    <textarea
                      value={projectForm.risks}
                      onChange={(e) => setProjectForm(prev => ({ ...prev, risks: e.target.value }))}
                      placeholder="Any potential risks or concerns for this project..."
                      rows={2}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
                      disabled={isSubmitting}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Project Notes
                    </label>
                    <textarea
                      value={projectForm.projectNotes}
                      onChange={(e) => setProjectForm(prev => ({ ...prev, projectNotes: e.target.value }))}
                      placeholder="Additional notes about this project..."
                      rows={3}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end space-x-4 pt-4 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddProject(false);
                    setEditingProject(null);
                    setSelectedClientForProject(null);
                    resetProjectForm();
                  }}
                  className="px-6 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isSubmitting}
                >
                  {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  <span>{editingProject ? 'Update Project' : 'Add Project'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default ClientsProjectsView;